import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RoleService } from '../role/role.service';
import {
  AddPlanFeatureBatchDetailItem,
  AddPlanFeatureBatchResponse,
  CreatePlanInput,
  CreatePlanResponse,
  DeletePlanFeatureResponse,
  DeletePlanResponse,
  GetPlanFeaturesByPlanIdResponse,
  GetPlansResponse,
  PlanDto,
  PlanFeatureDto,
  UpdatePlanFeatureBatchDetailItem,
  UpdatePlanFeaturesBatchResponse,
  UpdatePlanInput,
  UpdatePlanResponse
} from './plan.interface';

const GET_PLANS: DocumentNode = gql`
  query GetPlans($first: Int, $after: String, $accessLevel: DataAccessLevel) {
    GetPlans(first: $first, after: $after, accessLevel: $accessLevel) {
      nodes {
        id
        name
        description
        type
        maxUsers
        isActive
        validOnUtc
        validToUtc
        createdOnUtc
        modifiedOnUtc
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const GET_PLAN_FEATURES: DocumentNode = gql`
  query GetPlanFeaturesByPlanId($planId: UUID!, $accessLevel: DataAccessLevel) {
    GetPlanFeaturesByPlanId(planId: $planId, accessLevel: $accessLevel) {
      nodes {
        id
        isEnabled
        usageLimit
        limitPeriod
        plan {
          id
          name
        }
        feature {
          id
          name
          description
        }
      }
    }
  }
`;

const CREATE_PLAN: DocumentNode = gql`
  mutation CreatePlan($input: CreatePlanDtoInput!, $accessLevel: DataAccessLevel) {
    CreatePlan(input: $input, accessLevel: $accessLevel) {
      id
      name
      description
      type
      maxUsers
      isActive
    }
  }
`;

const UPDATE_PLAN: DocumentNode = gql`
  mutation UpdatePlan($input: UpdatePlanDtoInput!, $accessLevel: DataAccessLevel) {
    UpdatePlan(input: $input, accessLevel: $accessLevel) {
      id
      name
      description
      type
      maxUsers
      isActive
    }
  }
`;

const DELETE_PLAN: DocumentNode = gql`
  mutation DeletePlan($planId: UUID!, $accessLevel: DataAccessLevel) {
    DeletePlan(planId: $planId, accessLevel: $accessLevel)
  }
`;

const ADD_PLAN_FEATURE_BATCH: DocumentNode = gql`
  mutation AddPlanFeatureBatch($input: AddPlanFeatureBatchDtoInput!, $accessLevel: DataAccessLevel) {
    AddPlanFeatureBatch(input: $input, accessLevel: $accessLevel)
  }
`;

const UPDATE_PLAN_FEATURES_BATCH: DocumentNode = gql`
  mutation UpdatePlanFeaturesBatch($input: UpdatePlanFeatureBatchDtoInput!, $accessLevel: DataAccessLevel) {
    UpdatePlanFeaturesBatch(input: $input, accessLevel: $accessLevel)
  }
`;

const DELETE_PLAN_FEATURE: DocumentNode = gql`
  mutation DeletePlanFeature($id: UUID!, $accessLevel: DataAccessLevel) {
    DeletePlanFeature(id: $id, accessLevel: $accessLevel)
  }
`;

/** Service for managing plans and plan features via GraphQL API. */
@Injectable()
export class PlanService {
  /** Apollo client for GraphQL queries. */
  private readonly apollo: Apollo = inject(Apollo);

  /** Role service for determining data access level. */
  private readonly roleService: RoleService = inject(RoleService);

  /**
   * Gets all available plans with relay-style pagination.
   * @returns Observable<Array<PlanDto>> - Array of plan data from API
   */
  public getPlans(): Observable<Array<PlanDto>> {
    return this.apollo
      .query<GetPlansResponse>({
        query: GET_PLANS,
        variables: { accessLevel: this.roleService.getAccessLevel() },
        fetchPolicy: 'network-only'
      })
      .pipe(map((result): Array<PlanDto> => result.data?.GetPlans?.nodes ?? []));
  }

  /**
   * Fetch plan features for a specific plan.
   * @param planId - The plan ID
   * @returns Observable<Array<PlanFeatureDto>> - Array of plan features from API
   */
  public getPlanFeaturesById(planId: string): Observable<Array<PlanFeatureDto>> {
    return this.apollo
      .query<GetPlanFeaturesByPlanIdResponse>({
        query: GET_PLAN_FEATURES,
        variables: { planId, accessLevel: this.roleService.getAccessLevel() },
        fetchPolicy: 'network-only'
      })
      .pipe(map((result): Array<PlanFeatureDto> => result.data?.GetPlanFeaturesByPlanId?.nodes ?? []));
  }

  /**
   * Creates a new plan.
   * @param input - Plan creation input
   * @returns Observable<PlanDto | null> - The created plan, or null on failure
   */
  public createPlan(input: CreatePlanInput): Observable<PlanDto | null> {
    return this.apollo
      .mutate<CreatePlanResponse>({
        mutation: CREATE_PLAN,
        variables: { input, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): PlanDto | null => result.data?.CreatePlan ?? null));
  }

  /**
   * Updates an existing plan.
   * @param input - Plan update input
   * @returns Observable<PlanDto | null> - The updated plan, or null on failure
   */
  public updatePlan(input: UpdatePlanInput): Observable<PlanDto | null> {
    return this.apollo
      .mutate<UpdatePlanResponse>({
        mutation: UPDATE_PLAN,
        variables: { input, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): PlanDto | null => result.data?.UpdatePlan ?? null));
  }

  /**
   * Deletes a plan by ID.
   * @param id - The plan ID to delete
   * @returns Observable<boolean> - Success status
   */
  public deletePlan(id: string): Observable<boolean> {
    return this.apollo
      .mutate<DeletePlanResponse>({
        mutation: DELETE_PLAN,
        variables: { planId: id, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): boolean => result.data?.DeletePlan ?? false));
  }

  /**
   * Adds features to a plan in batch.
   * @param planId - The plan ID
   * @param planFeatures - Array of feature detail items to add
   * @returns Observable<boolean> - Success status
   */
  public addPlanFeatureBatch(
    planId: string,
    planFeatures: Array<AddPlanFeatureBatchDetailItem>
  ): Observable<boolean> {
    return this.apollo
      .mutate<AddPlanFeatureBatchResponse>({
        mutation: ADD_PLAN_FEATURE_BATCH,
        variables: {
          input: { planId, details: planFeatures },
          accessLevel: this.roleService.getAccessLevel()
        }
      })
      .pipe(map((result): boolean => result.data?.AddPlanFeatureBatch ?? false));
  }

  /**
   * Updates plan features in batch.
   * @param planId - The plan ID
   * @param details - Array of update detail items (with planFeatureId)
   * @returns Observable<boolean> - Success status
   */
  public updatePlanFeaturesBatch(
    planId: string,
    details: Array<UpdatePlanFeatureBatchDetailItem>
  ): Observable<boolean> {
    return this.apollo
      .mutate<UpdatePlanFeaturesBatchResponse>({
        mutation: UPDATE_PLAN_FEATURES_BATCH,
        variables: { input: { planId, details }, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): boolean => result.data?.UpdatePlanFeaturesBatch ?? false));
  }

  /**
   * Deletes a single plan feature.
   * @param id - The plan feature ID to delete
   * @returns Observable<boolean> - Success status
   */
  public deletePlanFeature(id: string): Observable<boolean> {
    return this.apollo
      .mutate<DeletePlanFeatureResponse>({
        mutation: DELETE_PLAN_FEATURE,
        variables: { id, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): boolean => result.data?.DeletePlanFeature ?? false));
  }
}
